import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  danger?: boolean;
}

interface ConfirmState {
  message: string;
  title: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (value: boolean) => void;
}

const ConfirmContext = createContext<(msg: string, opts?: ConfirmOptions) => Promise<boolean>>(
  () => Promise.resolve(false)
);

export function ConfirmProvider({ children }: React.PropsWithChildren<{}>) {
  const [state, setState] = useState<ConfirmState | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((message: string, opts: ConfirmOptions = {}): Promise<boolean> => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setState({
        message,
        title: opts.title || "Confirm",
        confirmLabel: opts.confirmLabel || "Confirm",
        danger: opts.danger ?? true,
        resolve,
      });
    });
  }, []);

  function handleResolve(value: boolean) {
    resolveRef.current?.(value);
    setState(null);
  }

  // Esc cancels, the way the native window.confirm() this replaced did.
  useEffect(() => {
    if (!state) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        resolveRef.current?.(false);
        setState(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
            zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => handleResolve(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "white", borderRadius: 12, padding: "24px 28px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)", width: 400, maxWidth: "90vw",
            }}
          >
            <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              {state.title}
            </h3>
            <p style={{ margin: "0 0 24px", fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
              {state.message}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => handleResolve(false)}
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "1px solid #e2e8f0",
                  background: "white", color: "#64748b", fontSize: 13, fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleResolve(true)}
                autoFocus
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "none",
                  background: state.danger ? "#dc2626" : "#1e3a8a",
                  color: "white", fontSize: 13, fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
